import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SubscriptionType } from '@app/utils/consts';
import { useAppDispatch } from '@app/redux/hooks';
import { getProfile } from '@app/services/user';
import { ActionTypes } from '@app/redux/types';
import { register } from '@app/services/auth';
import { setDefaultPaymentMethod } from '@app/services/plans';
import { Stripe, StripeElements } from '@stripe/stripe-js';

export const usePaymentPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [selectedPlanType, setSelectedPlanType] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getPlanType = (selectedPlan?: string) => {

    const planNameMap: Record<string, string> = {
      starter: SubscriptionType.FREE,
      professional: SubscriptionType.PRO,
      enterprise: SubscriptionType.GROWTH,
    };

    return planNameMap[selectedPlan ?? ''] ?? null;
  };

  useEffect(() => {
    const storedPlan = localStorage.getItem('selectedPlan');

    if (storedPlan) {
      setSelectedPlan(storedPlan);
      setSelectedPlanType(getPlanType(storedPlan));
      setIsLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      const retryPlan = localStorage.getItem('selectedPlan');
      if (retryPlan) {
        setSelectedPlan(retryPlan);
        setSelectedPlanType(getPlanType(retryPlan));
        setIsLoading(false);
      } else {
        setIsLoading(false);
        navigate('/welcome');
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [navigate]);

  const planDetails = selectedPlan
    ? {
        // starter: {
        //   name: 'Starter Plan',
        //   price: '$47',
        //   priceId: 'starter',
        //   planName: SubscriptionType.BASIC
        // },
        // professional: {
        //   name: 'Professional Plan',
        //   price: '$97',
        //   priceId: 'professional',
        //   planName: SubscriptionType.PROFESSIONAL
        // },
        // enterprise: {
        //   name: 'Enterprise Plan',
        //   price: '$127',
        //   priceId: 'enterprise',
        //   planName: SubscriptionType.ENTERPRISE
        // }
        starter: {
          name: 'Free Plan',
          price: '$0',
          priceId: 'starter',
          planName: SubscriptionType.FREE,
        },
        professional: {
          name: 'Pro Plan',
          price: '$97',
          priceId: 'professional',
          planName: SubscriptionType.PRO,
        },
        enterprise: {
          name: 'Growth Plan',
          price: '$197',
          priceId: 'enterprise',
          planName: SubscriptionType.GROWTH,
        },
      }[selectedPlan]
    : null;

  const handleStripeSuccess = async (stripe: Stripe, elements: StripeElements) => {
    setIsProcessing(true);
    try {
      const storedFormData = localStorage.getItem('signupFormData');
      const pendingBusinessData = localStorage.getItem('pendingBusinessData');

      if (storedFormData) {
        const parsedFormData = JSON.parse(storedFormData);

        const registerData = {
          firstName: parsedFormData.fullName.split(' ')[0] || '',
          lastName: parsedFormData.fullName.split(' ').slice(1).join(' ') || '',
          email: parsedFormData.email,
          password: parsedFormData.password,
          phoneNumber: parsedFormData.phoneNumber || '',
          business: {
            name: parsedFormData.businessName,
            email: parsedFormData.businessEmail || parsedFormData.email,
            address: parsedFormData.businessAddress,
            category: parsedFormData.businessCategory,
            planName: planDetails?.planName,
          },
          isTrial: false,
          isPaidPlan: true,
        };

        const registerResponse = await register(registerData);
        const clientSecret = registerResponse?.data?.user?.clientSecret;

        dispatch({ type: 'auth/save', payload: registerResponse?.data?.user });
        dispatch({ type: 'business/loadList', payload: registerResponse?.data?.businesses });
        dispatch({
          type: 'business/selectBusiness',
          payload: { id: registerResponse?.data?.businesses?.[0]?.id },
        });
        localStorage.setItem('accessToken', registerResponse?.data?.accessToken);

        if (clientSecret && stripe && elements) {
          const { error, setupIntent } = await stripe.confirmSetup({
            clientSecret,
            elements,
            confirmParams: {
              // return_url: `${window.location.origin}/sign-up-confirmation`,
              return_url: `${window.location.origin}/trial-sign-up-confirmation`,
            },
            redirect: 'if_required',
          });

          if (error) {
            toast.error(error.message || 'Payment setup failed');
            setIsProcessing(false);
            return;
          }

          if (setupIntent?.payment_method) {
            const paymentMethodId = typeof setupIntent.payment_method === 'string'
              ? setupIntent.payment_method
              : setupIntent.payment_method.id;
            await setDefaultPaymentMethod(paymentMethodId);
          }
        }

        if (registerResponse?.data?.googleAccessToken) {
          localStorage.setItem('googleAccessToken', registerResponse?.data?.googleAccessToken);
        }

        localStorage.removeItem('signupFormData');
        localStorage.removeItem('selectedPlan');
        localStorage.removeItem('selectedPlanType');

        // navigate('/sign-up-confirmation');
        navigate('/trial-sign-up-confirmation');
      } else if (pendingBusinessData) {
        const businessData = JSON.parse(pendingBusinessData);
        const { createBusiness } = await import('@app/services/business');

        const businessDataWithPlan = {
          ...businessData,
          planName: planDetails?.planName,
          isTrial: false,
        };

        const businessResponse = await createBusiness(businessDataWithPlan);

        const businessId = businessResponse?.data?.id || businessResponse?.data?.business?.id;

        if (businessId) {
          localStorage.setItem('pendingBusinessId', businessId);
          localStorage.setItem('pendingPlanType', planDetails?.planName || '');

          try {
            const userResponse = await getProfile();
            dispatch({ type: ActionTypes.AUTH_SAVE, payload: userResponse?.data?.user });
            dispatch({
              type: ActionTypes.BUSINESS_LOAD_LIST,
              payload: userResponse?.data?.businesses,
            });
          } catch (error) {
            console.error('Error refreshing user data:', error);
          }

          toast.success('Business created successfully!');
          navigate('/select-business?new=true');
        } else {
          throw new Error('Failed to create business - no business ID found in response');
        }

        localStorage.removeItem('pendingBusinessData');
        localStorage.removeItem('selectedPlan');
        localStorage.removeItem('selectedPlanType');
      } else {
        toast.error('No stored data found');
        setIsProcessing(false);
        return;
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error('Error processing payment');
      setIsProcessing(false);
    }
  };

  return {
    selectedPlan,
    selectedPlanType,
    planDetails,
    handleStripeSuccess,
    isProcessing,
    isLoading,
  };
};
